import React, { useState, useEffect, useRef } from 'react';
import { Plus, Edit2, Trash2, FileText, Stethoscope, BarChart3, Users, Upload, Image as ImageIcon, X } from 'lucide-react';
import { articlesAPI, consultationsAPI, statsAPI } from '../config/api';
import '../styles/AdminDashboard.css';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('consultations');
  const [consultations, setConsultations] = useState([]);
  const [articles, setArticles] = useState([]);
  const [stats, setStats] = useState({
    consultations: 0,
    articles: 0,
    doctors: 50,
    visitors: '10,000'
  });
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ 
    title: '', 
    description: '', 
    image: null,
    imagePreview: '',
    existingImage: '',
    category: '',
    read_time: '',
    doctor: '',
    status: 'منشور'
  });
  const fileInputRef = useRef(null);

  // دالة لتصحيح مسار الصور
  const getCorrectImagePath = (imagePath) => {
    if (!imagePath) return null;
    
    const baseUrl = 'https://darkslateblue-eel-151368.hostingersite.com';
    
    // إذا كان المسار يحتوي على النطاق الكامل بالفعل
    if (imagePath.startsWith('http')) {
      // إصلاح المسارات المكررة
      const corrected = imagePath.replace(
        /uploads\/(consultations|articles)\/uploads\/(consultations|articles)\//g,
        'uploads/$1/'
      );
      return corrected;
    }
    
    // إذا كان المسار نسبي فقط
    if (imagePath.startsWith('uploads/')) {
      return `${baseUrl}/storage/${imagePath}`;
    }
    
    // إذا كان المسار يبدأ بـ storage/
    if (imagePath.startsWith('storage/')) {
      return `${baseUrl}/${imagePath}`;
    }
    
    // الافتراضي
    return `${baseUrl}/storage/${imagePath}`;
  };

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      if (activeTab === 'consultations') {
        const response = await consultationsAPI.getAll();
        const data = response.data || response;
        
        // تصحيح مسارات الصور
        const correctedData = Array.isArray(data) 
          ? data.map(item => ({
              ...item,
              image: getCorrectImagePath(item.image)
            }))
          : [];
        
        setConsultations(correctedData);
      } else {
        const response = await articlesAPI.getAll();
        const data = response.data || response;
        
        // تصحيح مسارات الصور
        const correctedData = Array.isArray(data) 
          ? data.map(item => ({
              ...item,
              image: getCorrectImagePath(item.image)
            }))
          : [];
        
        setArticles(correctedData);
      }
      
      const statsData = await statsAPI.getStats();
      if (statsData) {
        const statsResult = statsData.data || statsData;
        setStats({
          consultations: statsResult.consultations || 0,
          articles: statsResult.articles || 0,
          doctors: statsResult.doctors || 50,
          visitors: statsResult.visitors || '10,000'
        });
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      if (activeTab === 'consultations') {
        setConsultations([]);
      } else {
        setArticles([]);
      }
      alert('حدث خطأ في تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, type) => {
    if (window.confirm('هل أنت متأكد من حذف هذا العنصر؟')) {
      try {
        if (type === 'consultation') {
          await consultationsAPI.delete(id);
          setConsultations(consultations.filter(item => item.id !== id));
        } else {
          await articlesAPI.delete(id);
          setArticles(articles.filter(item => item.id !== id));
        }
        alert('تم الحذف بنجاح');
        
        const statsData = await statsAPI.getStats();
        if (statsData) {
          const statsResult = statsData.data || statsData;
          setStats({
            consultations: statsResult.consultations || 0,
            articles: statsResult.articles || 0,
            doctors: statsResult.doctors || 50,
            visitors: statsResult.visitors || '10,000'
          });
        }
      } catch (err) {
        console.error('Error deleting:', err);
        alert('حدث خطأ أثناء الحذف');
      }
    }
  };

  const handleOpenAddModal = (type) => {
    setModalType('add');
    setEditingId(null);
    setShowModal(true);
    setFormData({ 
      title: '', 
      description: '', 
      image: null,
      imagePreview: '',
      existingImage: '',
      category: '',
      read_time: '',
      doctor: '',
      status: 'منشور'
    });
  };

  const handleOpenEditModal = (item, type) => {
    setModalType('edit');
    setEditingId(item.id);
    setShowModal(true);
    
    // تصحيح مسار الصورة قبل عرضها في المودال
    const correctedImage = getCorrectImagePath(item.image);
    
    if (type === 'consultation') {
      setFormData({
        title: item.title || '',
        description: item.description || '',
        image: null,
        imagePreview: '',
        existingImage: correctedImage || '',
        doctor: item.doctor || '',
        status: item.status || 'منشور',
        category: '',
        read_time: ''
      });
    } else {
      setFormData({
        title: item.title || '',
        description: item.description || '',
        image: null,
        imagePreview: '',
        existingImage: correctedImage || '',
        category: item.category || '',
        read_time: item.read_time || '',
        doctor: '',
        status: item.status || 'منشور'
      });
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('حجم الصورة يجب أن يكون أقل من 5 ميجابايت');
        return;
      }

      if (!file.type.startsWith('image/')) {
        alert('الرجاء اختيار صورة فقط');
        return;
      }

      setFormData(prev => ({
        ...prev,
        image: file,
        imagePreview: URL.createObjectURL(file),
        existingImage: ''
      }));
    }
  };

  const removeImage = () => {
    setFormData(prev => ({
      ...prev,
      image: null,
      imagePreview: '',
      existingImage: ''
    }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async () => {
    try {
      if (!formData.title || !formData.description) {
        alert('يرجى ملء جميع الحقول الإلزامية (العنوان والوصف)');
        return;
      }

      if (modalType === 'add' && !formData.image && !formData.existingImage) {
        alert('الرجاء اختيار صورة');
        return;
      }

      const submitFormData = new FormData();
      submitFormData.append('title', formData.title);
      submitFormData.append('description', formData.description);
      submitFormData.append('status', formData.status);
      
      if (formData.image) {
        submitFormData.append('image', formData.image);
      }
      
      const isConsultation = activeTab === 'consultations';
      if (isConsultation) {
        submitFormData.append('doctor', formData.doctor || 'د. جديد');
      } else {
        submitFormData.append('category', formData.category || 'عام');
        submitFormData.append('read_time', formData.read_time || '5 دقائق');
      }

      let result;
      const apiUrl = 'https://darkslateblue-eel-151368.hostingersite.com/api';
      const endpoint = isConsultation ? 'consultations' : 'articles';

      if (modalType === 'add') {
        const response = await fetch(`${apiUrl}/${endpoint}`, {
          method: 'POST',
          body: submitFormData,
          headers: {
            'Accept': 'application/json',
          }
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'حدث خطأ أثناء الإضافة');
        }

        result = await response.json();
        const newItem = result.data || result;
        
        // تصحيح مسار الصورة قبل إضافتها للقائمة
        const correctedItem = {
          ...newItem,
          image: getCorrectImagePath(newItem.image)
        };
        
        if (isConsultation) {
          setConsultations(prev => [correctedItem, ...prev]);
        } else {
          setArticles(prev => [correctedItem, ...prev]);
        }
      } else {
        const response = await fetch(`${apiUrl}/${endpoint}/${editingId}`, {
          method: 'POST',
          body: submitFormData,
          headers: {
            'Accept': 'application/json',
            'X-HTTP-Method-Override': 'PUT'
          }
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'حدث خطأ أثناء التعديل');
        }

        result = await response.json();
        const updatedItem = result.data || result;
        
        // تصحيح مسار الصورة قبل تحديث القائمة
        const correctedItem = {
          ...updatedItem,
          image: getCorrectImagePath(updatedItem.image)
        };
        
        if (isConsultation) {
          setConsultations(prev => prev.map(item => 
            item.id === editingId ? correctedItem : item
          ));
        } else {
          setArticles(prev => prev.map(item => 
            item.id === editingId ? correctedItem : item
          ));
        }
      }
      
      const statsData = await statsAPI.getStats();
      if (statsData) {
        const statsResult = statsData.data || statsData;
        setStats({
          consultations: statsResult.consultations || 0,
          articles: statsResult.articles || 0,
          doctors: statsResult.doctors || 50,
          visitors: statsResult.visitors || '10,000'
        });
      }
      
      setShowModal(false);
      setFormData({ 
        title: '', 
        description: '', 
        image: null,
        imagePreview: '',
        existingImage: '',
        category: '',
        read_time: '',
        doctor: '',
        status: 'منشور'
      });
      alert(modalType === 'add' ? 'تم الإضافة بنجاح' : 'تم التعديل بنجاح');
    } catch (err) {
      console.error('Error submitting:', err);
      alert(`حدث خطأ: ${err.message}`);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const statsData = [
    { icon: Stethoscope, label: 'الاستشارات', value: stats.consultations, color: 'blue' },
    { icon: FileText, label: 'المقالات', value: stats.articles, color: 'purple' },
    { icon: Users, label: 'الأطباء', value: stats.doctors, color: 'green' },
    { icon: BarChart3, label: 'الزوار', value: stats.visitors, color: 'orange' }
  ];

  const displayItems = activeTab === 'consultations' 
    ? (Array.isArray(consultations) ? consultations : [])
    : (Array.isArray(articles) ? articles : []);

  return (
    <div className="admin-dashboard" dir="rtl">
      {/* Sidebar */}
      <div className="admin-sidebar">
        <h2 className="sidebar-title">لوحة التحكم</h2>
        
        <nav className="sidebar-nav">
          <button
            onClick={() => setActiveTab('consultations')}
            className={`sidebar-button ${activeTab === 'consultations' ? 'active' : ''}`}
          >
            <Stethoscope className="sidebar-icon" />
            الاستشارات
          </button>
          
          <button
            onClick={() => setActiveTab('articles')}
            className={`sidebar-button ${activeTab === 'articles' ? 'active' : ''}`}
          >
            <FileText className="sidebar-icon" />
            المقالات
          </button>
        </nav>
      </div>

      {/* Main Content */}
      <div className="admin-main-content">
        {/* Stats Cards */}
        <div className="stats-grid">
          {statsData.map((stat, index) => (
            <div key={index} className="stat-card">
              <div className="stat-card-content">
                <div className="stat-info">
                  <p className="stat-label">{stat.label}</p>
                  <p className="stat-value">{stat.value}</p>
                </div>
                <div className={`stat-icon-wrapper ${stat.color}`}>
                  <stat.icon className="stat-icon" />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Content Area */}
        <div className="content-card">
          <div className="content-header">
            <h2 className="content-title">
              {activeTab === 'consultations' ? 'إدارة الاستشارات' : 'إدارة المقالات'}
            </h2>
            <button
              onClick={() => handleOpenAddModal(activeTab === 'consultations' ? 'consultation' : 'article')}
              className="add-button"
            >
              <Plus className="add-icon" />
              إضافة جديد
            </button>
          </div>

          {/* Table */}
          {loading ? (
            <div className="admin-loading-container">
              <div className="admin-loading-spinner"></div>
              <p className="admin-loading-text">جاري التحميل...</p>
            </div>
          ) : (
            <div className="admin-table-container">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>الصورة</th>
                    <th>العنوان</th>
                    <th>
                      {activeTab === 'consultations' ? 'الطبيب' : 'التصنيف'}
                    </th>
                    <th>الحالة</th>
                    <th className="actions-cell">الإجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {displayItems.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <div className="table-image">
                          <img 
                            src={item.image || 'https://via.placeholder.com/50x50/3b82f6/ffffff?text=IMG'} 
                            alt={item.title}
                            className="table-image-preview"
                            onError={(e) => {
                              console.error('فشل تحميل صورة الجدول:', item.image);
                              e.target.src = 'https://via.placeholder.com/50x50/3b82f6/ffffff?text=IMG';
                              
                              // محاولة مسار بديل
                              if (item.image) {
                                const altPath = item.image.replace(
                                  'https://darkslateblue-eel-151368.hostingersite.com/storage/',
                                  'https://darkslateblue-eel-151368.hostingersite.com/public/storage/'
                                );
                                setTimeout(() => {
                                  const tempImg = new Image();
                                  tempImg.onload = () => {
                                    e.target.src = altPath;
                                  };
                                  tempImg.src = altPath;
                                }, 100);
                              }
                            }}
                            onLoad={(e) => {
                              console.log('تم تحميل صورة الجدول بنجاح:', item.image);
                            }}
                          />
                        </div>
                      </td>
                      <td className="title-cell">
                        <div className="title-text" title={item.title}>
                          {item.title}
                        </div>
                      </td>
                      <td>
                        {activeTab === 'consultations' ? item.doctor : item.category}
                      </td>
                      <td>
                        <span className={`status-badge ${item.status === 'منشور' ? 'published' : 'draft'}`}>
                          {item.status}
                        </span>
                      </td>
                      <td className="actions-cell">
                        <div className="actions-buttons">
                          <button 
                            className="action-button edit-button"
                            onClick={() => handleOpenEditModal(
                              item, 
                              activeTab === 'consultations' ? 'consultation' : 'article'
                            )}
                          >
                            <Edit2 className="action-icon" />تعديل
                          </button>
                          <button
                            onClick={() => handleDelete(
                              item.id, 
                              activeTab === 'consultations' ? 'consultation' : 'article'
                            )}
                            className="action-button delete-button"
                          >
                            <Trash2 className="action-icon" />حذف
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {displayItems.length === 0 && (
                <div className="empty-state">
                  <FileText className="empty-state-icon" />
                  <p className="empty-state-text">
                    لا توجد {activeTab === 'consultations' ? 'استشارات' : 'مقالات'} حتى الآن
                  </p>
                  <button
                    onClick={() => handleOpenAddModal(activeTab === 'consultations' ? 'consultation' : 'article')}
                    className="empty-state-button"
                  >
                    <Plus className="add-icon" />
                    إضافة {activeTab === 'consultations' ? 'استشارة' : 'مقال'} جديد
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3 className="modal-title">
              {modalType === 'add' ? 'إضافة' : 'تعديل'} {activeTab === 'consultations' ? 'استشارة' : 'مقال'} 
              {modalType === 'edit' && ` (رقم: ${editingId})`}
            </h3>
            
            <div className="modal-form">
              <div className="form-group">
                <label className="form-label required">العنوان</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="form-input"
                  placeholder="أدخل العنوان هنا"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label required">الوصف</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="form-textarea"
                  rows="4"
                  placeholder="أدخل الوصف هنا"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">
                  الصورة {modalType === 'add' && <span className="required">*</span>}
                </label>
                
                <div className="image-upload-container">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageChange}
                    accept="image/*"
                    className="file-input"
                    style={{ display: 'none' }}
                  />
                  
                  <button
                    type="button"
                    onClick={triggerFileInput}
                    className="image-upload-button"
                  >
                    <Upload className="upload-icon" />
                    {formData.image || formData.existingImage ? 'تغيير الصورة' : 'اختيار صورة'}
                  </button>
                  
                  {(formData.imagePreview || formData.existingImage) && (
                    <div className="image-preview">
                      <img 
                        src={formData.imagePreview || formData.existingImage} 
                        alt="Preview" 
                        className="image-preview-img"
                        onError={(e) => {
                          console.error('فشل تحميل صورة المعاينة:', formData.existingImage);
                          e.target.src = 'https://via.placeholder.com/200x150/3b82f6/ffffff?text=Error';
                          
                          // محاولة مسار بديل للصورة الحالية
                          if (formData.existingImage && !formData.imagePreview) {
                            const altPath = formData.existingImage.replace(
                              'https://darkslateblue-eel-151368.hostingersite.com/storage/',
                              'https://darkslateblue-eel-151368.hostingersite.com/public/storage/'
                            );
                            setTimeout(() => {
                              const tempImg = new Image();
                              tempImg.onload = () => {
                                e.target.src = altPath;
                              };
                              tempImg.src = altPath;
                            }, 100);
                          }
                        }}
                        onLoad={(e) => {
                          console.log('تم تحميل صورة المعاينة بنجاح:', 
                            formData.imagePreview || formData.existingImage);
                        }}
                      />
                      <div className="image-info">
                        {formData.image && (
                          <>
                            <p className="image-file-name">{formData.image.name}</p>
                            <p className="image-file-size">
                              الحجم: {(formData.image.size / 1024).toFixed(2)} كيلوبايت
                            </p>
                          </>
                        )}
                        {formData.existingImage && !formData.image && (
                          <p className="image-file-name">الصورة الحالية</p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={removeImage}
                        className="remove-image-button"
                      >
                        <X className="remove-icon" />
                        إزالة الصورة
                      </button>
                    </div>
                  )}
                  
                  {!formData.imagePreview && !formData.existingImage && (
                    <div className="image-placeholder">
                      <ImageIcon className="placeholder-icon" />
                      <p className="placeholder-text">لم يتم اختيار صورة بعد</p>
                      <p className="placeholder-hint">الحجم الأقصى: 5 ميجابايت</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">الحالة</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  className="form-select"
                >
                  <option value="منشور">منشور</option>
                  <option value="مسودة">مسودة</option>
                </select>
              </div>

              {activeTab === 'consultations' ? (
                <div className="form-group">
                  <label className="form-label">الطبيب</label>
                  <input
                    type="text"
                    value={formData.doctor}
                    onChange={(e) => setFormData({...formData, doctor: e.target.value})}
                    className="form-input"
                    placeholder="أدخل اسم الطبيب"
                  />
                </div>
              ) : (
                <>
                  <div className="form-group">
                    <label className="form-label">التصنيف</label>
                    <input
                      type="text"
                      value={formData.category}
                      onChange={(e) => setFormData({...formData, category: e.target.value})}
                      className="form-input"
                      placeholder="أدخل التصنيف"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">وقت القراءة</label>
                    <input
                      type="text"
                      value={formData.read_time}
                      onChange={(e) => setFormData({...formData, read_time: e.target.value})}
                      className="form-input"
                      placeholder="5 دقائق"
                    />
                  </div>
                </>
              )}
              
              <div className="modal-buttons">
                <button
                  onClick={handleSubmit}
                  className="save-button"
                  disabled={!formData.title || !formData.description || (modalType === 'add' && !formData.image && !formData.existingImage)}
                >
                  {modalType === 'add' ? 'حفظ' : 'تحديث'}
                </button>
                <button
                  onClick={() => setShowModal(false)}
                  className="cancel-button"
                >
                  إلغاء
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;